import { useForm } from 'react-hook-form';
import { Checkbox, FormProvider, SelectTag, Table, TextField } from '../../../../../components';
import { Button, Container, Dialog, Grid, IconButton, Paper, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { getCurrentDateTime, getKeyPathValue, request, toast } from '../../../../../utils';

const HANDOVER_HEADER = [
  {
    header: 'IPD No.',
    accessor: 'ipd_no'
  },

  {
    header: 'UHID No',
    accessor: 'uhid'
  },

  {
    header: 'Patient Name',
    accessor: 'patient_name'
  },
  {
    header: 'Mobile No.',
    accessor: 'mobile_no'
  },
  {
    header: 'Adm Date',
    accessor: 'adm_date'
  }
];

const ITEM_DETAIL = [
  {
    header: 'Item Name',
    accessor: 'item_name'
  },

  {
    header: 'Op Qty.',
    accessor: 'op_qty'
  },

  {
    header: 'Rec. Qty',
    accessor: 'rec_qty'
  },
  {
    header: 'Iss_qty',
    accessor: 'iss_qty'
  },
  {
    header: 'Bal Qty',
    accessor: 'bal_qty'
  }
];

const initialOPDBillingDataObj = {
  data: [],
  loading: false
};

export const NursingHandoverForm = () => {
  const [detailList, setDetailList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        uhid: Yup.string().required(' '),
        patient_name: Yup.string().required(' '),
        opd_type: Yup.string().required(' '),
        ref_type: Yup.string().required(' '),
        doctor: Yup.string().required(' '),
        sponsor: Yup.string().required(' '),
        department: Yup.string().required(' '),
        bill: Yup.object().shape({
          rec_amt_1: Yup.number().min(0, ' '),
          rec_amt_2: Yup.number().min(0, ' '),
          pay_mode: Yup.string().required(' ')
        })
      })
    ),
    defaultValues: {
      date: getCurrentDateTime('date'),
      time: getCurrentDateTime('time'),
      bill: {
        out_sourced: 'No',
        rec_amt_1: 0,
        rec_amt_2: 0,
        tds_amt: 0,
        disc_amt: 0,
        gst_amt: 0,
        net_amt: 0
      }
    },
    mode: 'all'
  });
  const { handleSubmit, watch, reset, setValue } = methods;

  const formSubmit = async (formValues) => {
    setLoading(true);
    const payload = { uhid: formValues.uhid, ...formValues.bill, service_items: detailList };
    let editFlag = false;
    if (selectedRecord && currentAction === 'edit') {
      editFlag = true;
    }
    const response = await request(editFlag ? '/' : '/', {
      method: editFlag ? 'PUT' : 'POST',
      params: editFlag ? selectedRecord._id : null,
      body: payload
    });
    if (response?.success) {
      toast(`Successfully ${editFlag ? 'edited' : 'created'} `, { variant: 'success' });
      setSelectedRecord(response.data);
      setCurrentAction();
    } else {
      toast(`Failed to ${editFlag ? 'edit' : 'create'} `, { variant: 'error' });
    }
    setLoading(false);
  };

  return (
    <>
      <Paper sx={{ p: 0, flex: 1, borderRadius: 0, boxShadow: 'none' }}>
        <Container
          sx={{
            mt: 2,
            p: 0.2,
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: "100%",
            boxSizing: 'border-box',
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                pb: 0.5,
                mb: 3,
                borderBottom: '1px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3">
                Nursing Handover Form
              </Typography>

              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <Button variant="contained">Save</Button>
                <Button variant="contained">Exit</Button>
              </Stack>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6} sx={{ width: '40%' }}>
                <Grid container spacing={2} direction="column" sx={{ minHeight: '45rem' }}>
                  <Grid item sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Stack spacing={0.5} direction="row" alignItems="center">
                      <Typography sx={{ flex: 0.1 }}>Ward </Typography>
                      <SelectTag             formControlSX={{ flex: 0.6 }} name="ward_name" placeholder="Ward Name" />
                    </Stack>
                  </Grid>

                  <Grid item sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                    <Table
                      containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                      data={detailList}
                      columns={HANDOVER_HEADER}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} md={6} sx={{ width: '40%' }}>
                <Grid container spacing={2} direction="column" sx={{ minHeight: '45rem' }}>
                  <Grid item sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                    <Stack spacing={0.5} direction="row" alignItems="center">
                      <Typography sx={{ flex: 0.1 }}>Handover No.</Typography>
                      <TextField             sx={{ flex: 0.3 }} name="handover_no" disabled />
                      <Typography sx={{ flex: 0.1 }}>Date/Time</Typography>
                      <TextField             sx={{ flex: 0.2 }} name="date" disabled />
                      <TextField             sx={{ flex: 0.2 }} name="time" disabled />
                    </Stack>
                    <Stack spacing={0.5} direction="row" alignItems="center">
                      <Typography sx={{ flex: 0.1 }}>Handover By</Typography>
                      <SelectTag             formControlSX={{ flex: 0.3 }} name="handover_by" disabled />
                      <Typography sx={{ flex: 0.1 }}>Handover To</Typography>
                      <SelectTag             formControlSX={{ flex: 0.4 }} name="handover_to" />
                    </Stack>
                  </Grid>

                  <Grid item sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                    <Table
                      containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                      data={detailList}
                      columns={ITEM_DETAIL}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </FormProvider>
        </Container>
      </Paper>
    </>
  );
};
